import { useState } from 'react';
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CopyButton,
  Skeleton,
} from '@tinct/web-components';
import {
  createSsoCertificateSchema,
  ReadSsoCertificate,
  ReadSsoConnection,
  SSO_CERTIFICATE_MAX_LENGTH,
} from '@tinct/core-api-models';
import {
  useAddSsoCertificate,
  useDeleteSsoCertificate,
  useImportSsoConnectionMetadata,
} from '@tinct/core-api';
import { formatDate } from '../format-date';
import { RefreshCcwIcon, Trash2Icon, TriangleAlertIcon } from 'lucide-react';
import { toast } from 'sonner';
import { certificateExpiryState } from '../certificate-expiry';
import { toPemCertificate } from '../certificate-file';
import { useSsoCertificateList } from '../hooks/use-sso-certificate-list';
import { RemoveSsoCertificateModal } from '../modals/remove-sso-certificate-modal';
import { FileOrPasteInput } from './file-or-paste-input';

/** The API registers two at most: the room a rotation needs, and no more. */
const MAX_CERTIFICATES = 2;

export type SsoCertificatesCardProps = {
  organizationId: string;
  connection: ReadSsoConnection;
};

/** Enough of a fingerprint to tell two certificates apart in a dialog or a label. */
const shortFingerprint = (fingerprint: string | null | undefined) => {
  if (!fingerprint) return '—';

  return fingerprint.length > 16 ? `${fingerprint.slice(0, 16)}…` : fingerprint;
};

function ExpiryBadge({ certificate }: { certificate: ReadSsoCertificate }) {
  const state = certificateExpiryState(certificate, new Date());

  if (state.kind === 'expired') {
    return <Badge variant="destructive">Expired</Badge>;
  }
  if (state.kind === 'not_yet_valid') {
    return <Badge variant="warning">Not valid yet</Badge>;
  }
  if (state.kind === 'expiring') {
    return (
      <Badge variant="warning">
        {state.daysLeft === 1
          ? 'Expires tomorrow'
          : `Expires in ${state.daysLeft} days`}
      </Badge>
    );
  }
  if (state.kind === 'valid') {
    return <Badge variant="success">Valid</Badge>;
  }

  return null;
}

/**
 * The signing certificates of a SAML connection. Two at most, which is the whole point: register the
 * provider's new one, let it switch, then remove the old one — nobody is signed out in between.
 */
export function SsoCertificatesCard({
  organizationId,
  connection,
}: SsoCertificatesCardProps) {
  const [pem, setPem] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [certificateToRemove, setCertificateToRemove] =
    useState<ReadSsoCertificate | null>(null);

  const { data: certificates, isLoading } = useSsoCertificateList(
    organizationId,
    true,
  );

  const addCertificate = useAddSsoCertificate({
    onSuccess: () => {
      setPem('');
      toast.success('Certificate registered');
    },
  });

  const deleteCertificate = useDeleteSsoCertificate({
    onSuccess: () => {
      setCertificateToRemove(null);
      toast.success('Certificate removed');
    },
    onError: (error) => toast.error(error.error),
  });

  const importMetadata = useImportSsoConnectionMetadata({
    onSuccess: () =>
      toast.success("Applied what your provider's metadata announces"),
    // 422 (5511) when the document cannot be used, 409 without a metadata URL: its message says which.
    onError: (error) => toast.error(error.error),
  });

  const rows = certificates?.data ?? [];
  const validCount = rows.filter((row) => row.status === 'valid').length;
  const isFull = rows.length >= MAX_CERTIFICATES;
  // Non-null while the provider's metadata announces certificates the connection does not hold. Only
  // a connection described by a metadata address can be brought up to date in one click.
  const announcesOtherCertificates =
    !!connection.idpMetadataChangedAt && !!connection.idpMetadataUrl;

  const handleAdd = () => {
    const parsed = createSsoCertificateSchema.safeParse({ pem: pem.trim() });
    if (!parsed.success) {
      setValidationError(
        parsed.error.issues[0]?.message ?? 'Invalid certificate',
      );
      return;
    }
    setValidationError(null);
    addCertificate.mutate({ organizationId, data: parsed.data });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl">Signing certificates</CardTitle>
        <CardDescription>
          What responses from your identity provider are verified with. Two can
          be registered at once, so a rotation costs no downtime: add the new
          one, let your provider switch, remove the old one.
        </CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-6">
        {announcesOtherCertificates && (
          <Alert variant="important">
            <TriangleAlertIcon />
            <AlertDescription>
              <span className="flex flex-col items-start gap-3">
                <span>
                  Your provider's metadata has been announcing other
                  certificates than the ones registered since{' '}
                  {formatDate(connection.idpMetadataChangedAt)}. Nothing was
                  applied on its own — reading them in is a change of trust.
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => importMetadata.mutate({ organizationId })}
                  isLoading={importMetadata.isPending}
                >
                  <RefreshCcwIcon className="size-4" />
                  Apply the provider's current certificates
                </Button>
              </span>
            </AlertDescription>
          </Alert>
        )}

        {isLoading && <Skeleton className="h-24 w-full rounded-xl" />}

        {!isLoading && rows.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No certificate registered. Single sign-on cannot be used until one
            is.
          </p>
        )}

        {!isLoading &&
          rows.map((row) => (
            <div
              key={row.id}
              className="flex flex-col gap-3 rounded-2xl border p-4"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-3">
                    <span className="font-medium">
                      {row.subject ?? 'Certificate'}
                    </span>
                    <ExpiryBadge certificate={row} />
                  </div>
                  <span className="text-sm text-muted-foreground">
                    Valid from {formatDate(row.notBefore)} to{' '}
                    {formatDate(row.notAfter)}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCertificateToRemove(row)}
                  aria-label={`Remove certificate ${shortFingerprint(row.fingerprintSha256)}`}
                >
                  <Trash2Icon className="size-4 text-destructive" />
                </Button>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span>SHA-256</span>
                <span className="break-all font-mono">
                  {row.fingerprintSha256}
                </span>
                {row.fingerprintSha256 && (
                  <CopyButton value={row.fingerprintSha256} />
                )}
              </div>
            </div>
          ))}

        {!isFull && (
          <div className="flex flex-col gap-3 border-t pt-6">
            <FileOrPasteInput
              id="sso-saml-new-certificate"
              label={
                rows.length === 0
                  ? 'Register a certificate'
                  : 'Register a second certificate'
              }
              accept=".pem,.cer,.crt,application/x-x509-ca-cert,application/pkix-cert"
              placeholder="-----BEGIN CERTIFICATE-----"
              description="A binary .cer is converted here. RSA of 2048 bits or more, signed with SHA-256 or stronger."
              value={pem}
              onChange={setPem}
              maxLength={SSO_CERTIFICATE_MAX_LENGTH}
              readBytes={toPemCertificate}
              error={
                validationError ?? addCertificate.error?.error ?? undefined
              }
            />
            <div className="flex justify-end">
              <Button
                onClick={handleAdd}
                disabled={!pem.trim()}
                isLoading={addCertificate.isPending}
              >
                Add certificate
              </Button>
            </div>
          </div>
        )}

        {isFull && (
          <p className="border-t pt-6 text-sm text-muted-foreground">
            Two certificates are registered, which is the most a connection
            holds. Remove the one your provider no longer signs with to make
            room for the next.
          </p>
        )}
      </CardContent>

      {certificateToRemove && (
        <RemoveSsoCertificateModal
          open
          onOpenChange={(open) => !open && setCertificateToRemove(null)}
          label={
            certificateToRemove.subject ??
            shortFingerprint(certificateToRemove.fingerprintSha256)
          }
          isLastValid={
            certificateToRemove.status === 'valid' && validCount === 1
          }
          isRemoving={deleteCertificate.isPending}
          onConfirm={() =>
            certificateToRemove.id &&
            deleteCertificate.mutate({
              organizationId,
              certificateId: certificateToRemove.id,
            })
          }
        />
      )}
    </Card>
  );
}
